/**
 * The locales the app ships (`D26`, `D267`): en-US is the base, pt-BR the
 * first layer, and five translated locales on top. Every tag carries a
 * region, because each is one written variety and not the generic language:
 * US English, Brazilian and European Portuguese, neutral Latin-American
 * Spanish (`419` is the UN M.49 code for Latin America), and the French,
 * German and Italian of France, Germany and Italy.
 *
 * The order is the language picker's order. `@repo/contract`'s `appLocale`
 * is the same list, restated because the app must not depend on a Worker's
 * contract for its startup path; `locale.test.ts` holds the two together.
 */
export const SUPPORTED_LOCALES = [
    "en-US",
    "pt-BR",
    "es-419",
    "pt-PT",
    "fr-FR",
    "de-DE",
    "it-IT",
];
/** English is the base locale, so it is also the fallback for everything. */
export const BASE_LOCALE = "en-US";
/**
 * Portuguese written outside Brazil. Every country where Portuguese is
 * official, Brazil aside, writes the European norm, so a phone set to
 * `pt-AO` or `pt-MZ` reads pt-PT rather than pt-BR.
 */
const EUROPEAN_PORTUGUESE_REGIONS = new Set([
    "PT",
    "AO",
    "MZ",
    "CV",
    "GW",
    "ST",
    "TL",
    "MO",
    "GQ",
    "LU",
    "CH",
]);
/** One shipped locale per language subtag (Portuguese is decided apart). */
const BY_LANGUAGE = {
    en: "en-US",
    es: "es-419",
    fr: "fr-FR",
    de: "de-DE",
    it: "it-IT",
};
const REGION = /^[A-Za-z]{2}$/;
/**
 * Map the device's preference list onto a locale we actually have.
 *
 * Matching is by language subtag, not by exact tag: Android reports `pt-BR`,
 * `pt_BR`, `pt`, and on some devices `pt-Latn-BR`, and all of them mean the
 * same thing to us. An exact-tag match would leave a Brazilian phone reading
 * English, which is the failure this function exists to prevent. The same holds
 * on the other side: `en-GB` and a bare `en` both resolve to `en-US`, every
 * Spanish (`es-ES` included) to `es-419`, `fr-CA` to `fr-FR`, `de-AT` to
 * `de-DE`.
 *
 * Portuguese is the one language with two varieties, and the REGION decides:
 * Portugal and the other countries that write the European norm get pt-PT;
 * Brazil, a bare `pt` and a region we cannot place get pt-BR, which is what
 * every Portuguese phone got before pt-PT existed (`D267`).
 *
 * The list is scanned in order, because that order is the user's own ranking in
 * the system settings: a phone set to `[ja, pt-BR, en]` should get Portuguese,
 * not English.
 */
export function resolveLocale(preferences) {
    for (const preference of preferences) {
        const [language, ...rest] = subtagsOf(preference);
        if (language === "pt") {
            const region = rest.find((part) => REGION.test(part));
            return region && EUROPEAN_PORTUGUESE_REGIONS.has(region.toUpperCase())
                ? "pt-PT"
                : "pt-BR";
        }
        const shipped = language ? BY_LANGUAGE[language] : undefined;
        if (shipped) {
            return shipped;
        }
    }
    return BASE_LOCALE;
}
function subtagsOf(tag) {
    // Android hands back both `pt-BR` and `pt_BR` depending on the API used.
    const [language = "", ...rest] = tag.replace(/_/g, "-").split("-");
    return [language.toLowerCase(), ...rest];
}
/** Type guard for a persisted or hand-entered locale, which may be anything. */
export function isSupportedLocale(value) {
    return (typeof value === "string" &&
        SUPPORTED_LOCALES.includes(value));
}
//# sourceMappingURL=locale.js.map